import { Fragment, useId, useRef, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router'

import logo from '../assets/logo.svg'
import mark from '../assets/mark.svg'
import { forgetNamedClasses } from '../annotation/namedClasses'
import { useApi } from '../lib/api'
import { useEducation } from '../lib/education'
import { useI18n, type MessageKey } from '../lib/i18n'
import { RELEASE } from '../lib/release'
import { useSession } from '../lib/session'
import { logOut } from '../lib/sessionEnd'
import { useSiteToken } from '../lib/token'
import { organizes } from '../lib/types'
import { WORKING } from '../upload/job'
import { leadingJob } from '../upload/jobs'
import { percentOf, useUploadsState } from '../upload/UploadsProvider'
import { LegalMenu } from '../pages/LegalPage'
import { NotificationBell } from '../pages/NotificationsPage'
import { Avatar } from './Avatar'
import { Icon, type IconName } from './Icon'
import { LanguageToggle } from './LanguageToggle'
import { TokenStatus } from './TokenStatus'

interface Entry {
  to: string
  label: MessageKey
  icon: IconName
}

const IMAGES: Entry = { to: '/images', label: 'images', icon: 'images' }
const READING: Entry = { to: '/assignments', label: 'reading', icon: 'reading' }
const COORDINATION: Entry = { to: '/work', label: 'coordination', icon: 'jobs' }
const COLLECTIONS: Entry = { to: '/collections', label: 'collections', icon: 'collections' }
const PROFILES: Entry = { to: '/labeling-profiles', label: 'labelingProfiles', icon: 'profiles' }
const SHARED: Entry = { to: '/shared', label: 'sharedWithMe', icon: 'shared' }
const UPLOADS: Entry = { to: '/uploads', label: 'uploads', icon: 'upload' }
const EDUCATION: Entry = { to: '/education', label: 'education', icon: 'education' }
const BENCHMARKS: Entry = { to: '/benchmarks', label: 'benchmarks', icon: 'benchmarks' }
const FORUM: Entry = { to: '/forum', label: 'forum', icon: 'forum' }
const SCHEDULING: Entry = { to: '/scheduling', label: 'scheduling', icon: 'calendar' }
const MANAGE: readonly Entry[] = [{ to: '/sites', label: 'sites', icon: 'sites' }]
const CATALOG: Entry = { to: '/catalog', label: 'labelCatalog', icon: 'labels' }
const PLATFORM: readonly Entry[] = [
  { to: '/accounts', label: 'users', icon: 'people' },
  { to: '/models', label: 'aiModels', icon: 'models' },
  { to: '/audit', label: 'auditLog', icon: 'audit' },
  { to: '/status', label: 'platformStatus', icon: 'status' },
  { to: '/speech-bench', label: 'speechBench', icon: 'mic' },
]

/**
 * The navigation beside every signed-in page, grouped by what the user does there, with the site token,
 * the manual and legal texts (NTF-03, NTF-04), the user's own links and the build's release at its foot. On pages with images it folds to a rail of icons (`collapsed`);
 * the names stay for screen readers and as tooltips. Narrow windows show only the logo and a menu button, so the page keeps the
 * screen; the menu covers the page until a link is followed, Escape is pressed or the button is pressed again.
 */
export function Sidebar({ collapsed }: { collapsed: boolean }) {
  const { t } = useI18n()
  const { me } = useSession()
  // Education for those cleared for it, educators and platform admins (EDU-01, EDU-02).
  const education = useEducation()
  const educating = me.is_platform_admin || (education.data?.roles.length ?? 0) > 0
  // Uploads for those enrolled for a site token, or with jobs in this tab (TOK-08).
  const { jobs } = useUploadsState()
  const uploading = (useSiteToken().keys.data?.length ?? 0) > 0 || jobs.length > 0
  const leading = leadingJob(jobs)
  const percent = leading && WORKING.has(leading.phase) ? percentOf(leading) : undefined
  // Benchmarks for those who coordinate one or are invited (BEN-02).
  const benchmarking = (useApi<unknown[]>('/benchmarks').data?.length ?? 0) > 0
  // The work of the sites they organize, its tasks, frozen collections and contributors (ROLE-09, D41).
  const coordinating = me.sites.some(organizes)
  // The label catalog for its editors, platform admins who appoint them, and site admins, who add attributes (LBL-02, ROLE-10).
  const managesCatalog = me.catalog_editor || me.is_platform_admin || me.sites.some((site) => site.role === 'site_admin')
  const work = [
    IMAGES,
    COLLECTIONS,
    READING,
    ...(coordinating ? [COORDINATION] : []),
    PROFILES,
    SHARED,
    ...(uploading ? [UPLOADS] : []),
    ...(benchmarking ? [BENCHMARKS] : []),
    ...(educating ? [EDUCATION] : []),
    FORUM,
    SCHEDULING,
  ]
  const groups: readonly (readonly [MessageKey, readonly Entry[]])[] = [
    ['navWork', work],
    ['navManage', [...MANAGE, ...(managesCatalog ? [CATALOG] : [])]],
    ...(me.is_platform_admin ? ([['navPlatform', PLATFORM]] as const) : []),
  ]
  const tip = (label: string) => (collapsed ? label : undefined)
  // Open on the page it was opened on, so any way of leaving the page closes it.
  const { pathname } = useLocation()
  const [openOn, setOpenOn] = useState<string | null>(null)
  const open = openOn === pathname
  const toggle = useRef<HTMLButtonElement>(null)
  const menu = useId()

  const logout = () => {
    forgetNamedClasses() // the free-text classes the user named end with the session (LBL-08)
    return logOut()
  }

  return (
    <aside
      className={open ? 'sidebar open' : 'sidebar'}
      onClick={(event) => (event.target as Element).closest('a') && setOpenOn(null)}
      onKeyDown={(event) => {
        if (event.key !== 'Escape' || !open) return
        setOpenOn(null)
        toggle.current?.focus()
      }}
    >
      <div className="sidebar-head">
        <Link to="/images" className="wordmark">
          <img src={collapsed ? mark : logo} alt="fracture.ai" />
          <small>{t('appTagline')}</small>
        </Link>
        <button
          ref={toggle}
          type="button"
          className="menu-toggle"
          aria-label={t('menu')}
          aria-expanded={open}
          aria-controls={`${menu}-nav ${menu}-foot`}
          onClick={() => setOpenOn(open ? null : pathname)}
        >
          <Icon name={open ? 'close' : 'menu'} />
        </button>
      </div>
      <nav id={`${menu}-nav`} aria-label={t('mainNavigation')}>
        {groups.map(([group, entries]) => (
          <Fragment key={group}>
            <p className="nav-group" id={`nav-${group}`}>
              {t(group)}
            </p>
            <ul className="nav-list" aria-labelledby={`nav-${group}`}>
              {entries.map((entry) => (
                <li key={entry.to}>
                  <NavLink to={entry.to} className="nav-item" title={tip(t(entry.label))}>
                    <Icon name={entry.icon} />
                    <span className="nav-label">{t(entry.label)}</span>
                    {entry === UPLOADS && percent !== undefined && <span className="nav-progress">{percent} %</span>}
                  </NavLink>
                </li>
              ))}
            </ul>
          </Fragment>
        ))}
      </nav>
      <div id={`${menu}-foot`} className="sidebar-foot">
        <TokenStatus collapsed={collapsed} />
        <NavLink to="/help" className="nav-item" title={tip(t('userManual'))}>
          <Icon name="help" />
          <span className="nav-label">{t('userManual')}</span>
        </NavLink>
        <LegalMenu collapsed={collapsed} />
        <NotificationBell collapsed={collapsed} />
        <NavLink to="/profile" className="nav-item me" title={tip(`${me.display_name} · ${t('profile')}`)}>
          <Avatar name={me.display_name} photo={me.photo} className="initials" />
          <span className="nav-label">
            {me.display_name}
            <span className="visually-hidden"> · {t('profile')}</span>
          </span>
        </NavLink>
        <div className="sidebar-end">
          <button type="button" className="nav-item" title={tip(t('logout'))} onClick={() => void logout()}>
            <Icon name="logout" />
            <span className="nav-label">{t('logout')}</span>
          </button>
          <LanguageToggle />
        </div>
        <p className="sidebar-version">
          <span className="visually-hidden">{t('version')} </span>
          {RELEASE}
        </p>
      </div>
    </aside>
  )
}
